/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { P } from "@cd2/text"
import React, { useRef, useState } from "react"
import { useLocation } from "react-router-dom"
import { LinkBaseButton } from "../../../Components/Buttons/LinkButton"
import { brandColors, themeColors } from "../../../Components/EOSThemeProvider"
import { useWebsiteUser } from "../../../Components/WebsiteProvider"
import { customerProductFilter } from "../../../constants/salesRestrictedProductFilter"
import { useAsyncEffect } from "../../../hooks/useAsyncEffect"
import { eRoutes } from "../../../routes"
import { IRecord } from "../../../types/schema"
import {
  getBulkProductSuggestedPrice,
  getProductSuggestedPrice,
} from "../../../utils/getProductSuggestedPrice"
import { savePostLoginRedirect } from "../../../utils/postLoginRedirect"
import { currencyNameToSymbol, formatPrice } from "../../../utils/utils"
import {
  formatClearancePrice,
  getClearancePriceForProductMaster,
} from "../../../../shared/operatingRegions"
import { ClearancePriceDisplay } from "./ClearancePriceDisplay"
import { ExclVatLabel } from "./ExclVatLabel"

export interface ICustomerPrice {
  price: number | null | undefined
  discount: number | null | undefined
  formattedPrice?: string
  /** Only set for clearance pricing */
  isClearance?: boolean
  originalPrice?: number | null
  originalFormattedPrice?: string
}

/**
 * Returns undefined if customer pricing setting is not enabled
 * Returns { discount: undefined, price: undefined, formattedPrice: undefined } if no pricing data
 * Returns { discount: number, price: number, formattedPrice: string } if pricing data IS available
 */
export function useCustomerProductPricing() {
  const { currentUser, currency } = useWebsiteUser()
  const skeem = window.skeem

  return async function ({
    productMasterId,
    productId,
    customerId,
    overridePriceViewing,
  }: (
    | { productId?: never; productMasterId: string }
    | { productId: string; productMasterId?: never }
  ) & { customerId?: string; overridePriceViewing?: boolean }): Promise<
    ICustomerPrice | undefined
  > {
    if (!(customerId || currentUser?.customer?.canViewPricing)) {
      return
    }
    if (customerId) {
      const customer = await skeem.fetch2<IRecord<"customers", "canViewPricing">>({
        first: {
          name: "customers",
          attributes: ["canViewPricing"],
          filter: { matches: { id: customerId } },
        },
      })
      if (!customer.canViewPricing && !overridePriceViewing) {
        return
      }
    }
    let product: IRecord<"products"> | undefined = undefined
    // If given a product master id - look up the product from it
    if (productMasterId) {
      const productMaster = await skeem.fetch2<{
        clearance_price_uk_pennies?: number | null
        clearance_price_ireland_pennies?: number | null
        clearance_was_price_uk_pennies?: number | null
        clearance_was_price_ireland_pennies?: number | null
        product?: IRecord<"products">
      }>({
        first: {
          name: "product_masters",
          attributes: [
            "clearance_price_uk_pennies",
            "clearance_price_ireland_pennies",
            "clearance_was_price_uk_pennies",
            "clearance_was_price_ireland_pennies",
            {
              as: "product",
              value: {
                first: {
                  path: [
                    {
                      name: "products",
                      filter: {
                        and: [
                          customerProductFilter(currentUser.customer?.id).product,
                          { matches: { active: true } },
                          { matches: { deleted: false } },
                          { matches: { branch: currentUser.branch.id } },
                        ],
                      },
                    },
                  ],
                },
              },
            },
          ],
          filter: { matches: { id: productMasterId } },
        },
      })
      if (!productMaster?.product && currentUser.customer?.id) {
        const universalMaster = await skeem.fetch2<{
          universal_price_band_products: IRecord<"universal_price_band_products", "price_pennies">[]
        }>({
          first: {
            name: "product_masters",
            attributes: [
              {
                name: "universal_price_band_products",
                attributes: ["price_pennies"],
                filter: {
                  gte: [
                    {
                      count: {
                        path: [
                          "universal_price_band",
                          {
                            name: "customers",
                            filter: { matches: { id: currentUser.customer.id } },
                          },
                        ],
                      },
                    },
                    { value: 1 },
                  ],
                },
              },
            ],
            filter: { matches: { id: productMasterId } },
          },
        })

        if (currentUser.branch?.id) {
          const clearance = productMaster
            ? getClearancePriceForProductMaster(productMaster, currentUser.branch.id)
            : null
          if (clearance) {
            const customerCompareAtPrice =
              universalMaster?.universal_price_band_products?.[0]?.price_pennies ?? null
            const wasPrice = clearance.was_price_pennies ?? null
            const compareAtPrice = customerCompareAtPrice ?? wasPrice
            const priceCurrency = clearance.currency_name
              ? currencyNameToSymbol(clearance.currency_name)
              : currency

            const result: ICustomerPrice = {
              price: clearance.price_pennies,
              discount:
                compareAtPrice != null && compareAtPrice > clearance.price_pennies
                  ? Math.round((1 - clearance.price_pennies / compareAtPrice) * 100)
                  : null,
              formattedPrice: formatClearancePrice(clearance),
              isClearance: true,
            }
            if (compareAtPrice != null && compareAtPrice > clearance.price_pennies) {
              result.originalPrice = compareAtPrice
              result.originalFormattedPrice = formatPrice(compareAtPrice, {
                currency: priceCurrency,
                isPennies: true,
              })
            }
            return result
          }
        }

        const price = universalMaster?.universal_price_band_products?.[0]?.price_pennies
        if (price) {
          return {
            discount: null,
            price,
            formattedPrice: formatPrice(price, { currency, isPennies: true }),
          }
        }
      } else {
        product = productMaster?.product
      }
    }
    // if given a product id - check it exists
    if (productId) {
      product = await skeem.fetch2<IRecord<"products">>({
        first: {
          name: "products",
          filter: { matches: { id: productId } },
        },
      })
    }
    if (!product) {
      return new Promise(resolve =>
        resolve({ discount: undefined, price: undefined, formattedPrice: undefined }),
      )
    }

    const customer = customerId ? { id: customerId } : currentUser.customer
    if (!customer?.id) {
      return {
        price: undefined,
        discount: undefined,
        formattedPrice: undefined,
      }
    }

    const price = await getProductSuggestedPrice(
      skeem,
      { customer: { id: customer.id } },
      product.id,
    )

    console.log("FETCH CUSTOMER PRICE", { price })

    if (!price || !price.customerPrice) {
      return undefined
    }

    const priceCurrency = price.currency_name ? currencyNameToSymbol(price.currency_name) : currency
    const result: ICustomerPrice = {
      price: price.customerPrice,
      discount: price.customerPriceDiscountPercent,
      formattedPrice:
        price.customerPrice != null
          ? formatPrice(price.customerPrice, { currency: priceCurrency, isPennies: true })
          : undefined,
    }
    if (price.sourceType === "clearance") {
      result.isClearance = true
      result.originalPrice = price.standardPrice
      if (
        price.standardPrice != null &&
        price.standardPrice > 0 &&
        price.customerPrice != null &&
        price.standardPrice > price.customerPrice
      ) {
        result.originalFormattedPrice = formatPrice(price.standardPrice, {
          currency: priceCurrency,
          isPennies: true,
        })
      }
    }
    return result
  }
}

export function useBulkCustomerProductMasterPricing({
  productMasterIds,
  customerId,
}: {
  productMasterIds: string[]
  customerId?: string
}) {
  const { currentUser, currency } = useWebsiteUser()
  const skeem = window.skeem
  const [pricingCache, setPricingCache] = useState<Record<string, ICustomerPrice>>({})
  const keyRef = useRef(Math.random())

  async function getPricing(): Promise<ICustomerPrice | undefined> {
    const customer = customerId ? { id: customerId } : currentUser.customer
    if (!customer?.id || !currentUser?.customer?.canViewPricing) {
      console.log("Unloading cache 1")
      setPricingCache({})
      return
    }
    if (customerId) {
      const customer = await skeem.fetch2<IRecord<"customers", "canViewPricing">>({
        first: {
          name: "customers",
          attributes: ["canViewPricing"],
          filter: { matches: { id: customerId } },
        },
      })
      if (!customer.canViewPricing) {
        console.log("Unloading cache 2")
        setPricingCache({})
        return
      }
    }
    let products: IRecord<"products">[] | undefined = undefined

    const productMasters = await skeem.fetch2<{ id: string; product?: IRecord<"products"> }[]>({
      model: {
        name: "product_masters",
        attributes: [
          {
            as: "product",
            value: {
              first: {
                path: [
                  {
                    name: "products",
                    filter: {
                      and: [
                        customerProductFilter(currentUser.customer?.id).product,
                        { matches: { active: true } },
                        { matches: { deleted: false } },
                        { matches: { branch: currentUser.branch.id } },
                      ],
                    },
                  },
                ],
              },
            },
          },
        ],
        filter: { matches: { id: { or: productMasterIds } } },
      },
    })
    products = productMasters
      // Filtering out any product ids we have already loaded
      .filter(master => !Object.keys(pricingCache).includes(master.id))
      .map(p => p.product)
      .filter(Boolean)

    if (!products.length) {
      return
    }
    const key = Math.random()
    keyRef.current = key

    const productIds = products.map(v => v.id)
    const productData = await getBulkProductSuggestedPrice(skeem, { customer }, productIds)

    const pricingCacheData: Record<string, ICustomerPrice> = {}
    for (const productId of productIds) {
      const customerPrice = productData[productId]
      if (!customerPrice) {
        continue
      }
      const productMasterId = productMasters.find(v => v.product?.id === productId)?.id
      if (!productMasterId) {
        continue
      }

      const priceCurrency = customerPrice.currency_name
        ? currencyNameToSymbol(customerPrice.currency_name)
        : currency
      const result: ICustomerPrice = {
        price: customerPrice.customerPrice,
        discount: customerPrice.customerPriceDiscountPercent,
        formattedPrice:
          customerPrice.customerPrice != null
            ? formatPrice(customerPrice.customerPrice, { currency: priceCurrency, isPennies: true })
            : undefined,
      }
      if (customerPrice.sourceType === "clearance") {
        result.isClearance = true
        result.originalPrice = customerPrice.standardPrice
        if (
          customerPrice.standardPrice != null &&
          customerPrice.standardPrice > 0 &&
          customerPrice.customerPrice != null &&
          customerPrice.standardPrice > customerPrice.customerPrice
        ) {
          result.originalFormattedPrice = formatPrice(customerPrice.standardPrice, {
            currency: priceCurrency,
            isPennies: true,
          })
        }
      }
      pricingCacheData[productMasterId] = result
    }
    if (keyRef.current === key) {
      console.log("Setting price cache")
      setPricingCache(currentData => ({ ...(currentData ?? {}), ...pricingCacheData }))
    }
  }

  useAsyncEffect(async () => {
    await getPricing()
  }, [JSON.stringify(productMasterIds), customerId])

  return pricingCache
}

export function CustomerPriceLoader({
  productId,
  productMasterId,
  render,
  showNoPrice,
  customerId,
}: (
  | { productId?: never; productMasterId: string }
  | { productId: string; productMasterId?: never }
) & {
  render?: (
    price?: ICustomerPrice,
    canViewPricing?: boolean | null,
    loading?: boolean,
  ) => React.ReactNode
  showNoPrice?: boolean
  customerId?: string
}) {
  const skeem = window.skeem
  const { currentUser } = useWebsiteUser()
  const [hasLoaded, setHasLoaded] = useState(false)
  const getCustomerProductPrice = useCustomerProductPricing()
  const [price, setPrice] = useState<ICustomerPrice>()
  const [canViewPricing, setCanViewPricing] = useState<boolean | null>(null)

  async function loadCanViewPricing() {
    if (!render) {
      return
    }
    // if has already loaded, skip reloading
    if (canViewPricing !== null) {
      return
    }
    // if no customer id, check if current user can view pricing
    if (!customerId) {
      // if not loaded, skip and retry once has
      if (!currentUser) {
        return
      }
      setCanViewPricing(!!currentUser?.customer?.canViewPricing)
    } else {
      // if customer id, check if customer can view pricing
      const customerCanViewPricing = await skeem.fetch2<IRecord<"customers", "canViewPricing">>({
        first: {
          name: "customers",
          matches: { id: customerId },
          attributes: ["canViewPricing"],
        },
      })
      setCanViewPricing(!!customerCanViewPricing?.canViewPricing)
    }
  }
  useAsyncEffect(loadCanViewPricing, [customerId, currentUser?.customer?.canViewPricing])

  useAsyncEffect(async () => {
    try {
      setHasLoaded(false)
      const price = await getCustomerProductPrice({ productId, customerId, productMasterId } as any)
      setPrice(price)
    } catch (e) {
      console.error(e)
      setPrice(undefined)
    } finally {
      setHasLoaded(true)
    }
  }, [productId])

  if (render) {
    return <React.Fragment>{render(price, canViewPricing, !hasLoaded)}</React.Fragment>
  }

  if (!hasLoaded) {
    return null
  }

  if (!price) {
    return null
  }

  return (
    <React.Fragment>
      {price?.price ? (
        <div>{price.formattedPrice}</div>
      ) : showNoPrice ? (
        <div>No pricing available for this product</div>
      ) : null}
    </React.Fragment>
  )
}

/** "Login for price" CTA for anonymous visitors on product cards / pages */
export function LoginForPriceButton({ size = "normal" }: { size?: "normal" | "large" }) {
  const location = useLocation()

  return (
    <div
      onClick={e => {
        // Product cards wrap content in a Link — don't navigate to the product page
        e.stopPropagation()
        savePostLoginRedirect(location.pathname + location.search + location.hash)
      }}
    >
      <LinkBaseButton
        to={eRoutes["/sign-in"]}
        theme={{
          boxShadow: "none",
          borderRadius: 2,
          borderColor: "transparent",
          buttonColor: brandColors.mint,
          hoverColor: brandColors.darkMint,
          color: brandColors.night,
          fontSize: size === "large" ? 16 : 14,
          padding: {
            sides: size === "large" ? 18 : 12,
            topAndBottom: size === "large" ? 10 : 8,
          },
          weight: 600,
        }}
      >
        Login for price
      </LinkBaseButton>
    </div>
  )
}

/** Shared price line for customer product cards and product show pages */
export function CustomerProductPriceDisplay({
  price,
  canViewPricing,
  isLoading,
  size = "normal",
  showExclVat,
}: {
  price?: ICustomerPrice
  canViewPricing?: boolean | null
  isLoading?: boolean
  size?: "normal" | "large"
  /** Adds an "excl. VAT" note after the price - only shown when there is a price to qualify */
  showExclVat?: boolean
}) {
  const { isAnonymous } = useWebsiteUser()
  const hasPrice = !!(canViewPricing && price?.formattedPrice)

  if (isLoading) {
    return (
      <P
        color="black"
        size={size === "large" ? "large" : "normal"}
        blockSpacing={0}
        css={
          size === "large"
            ? css`
                font-size: 18px;
                margin: 0;
              `
            : undefined
        }
      >
        <b>Price: </b>
        <span
          css={css`
            color: ${themeColors.grey};
            font-weight: ${size === "large" ? 200 : "inherit"};
          `}
        >
          -
        </span>
      </P>
    )
  }

  if (!canViewPricing && isAnonymous) {
    return <LoginForPriceButton size={size} />
  }

  return (
    <P
      color="black"
      size={size === "large" ? "large" : "normal"}
      blockSpacing={0}
      css={
        size === "large"
          ? css`
              font-size: 18px;
              margin: 0;
            `
          : undefined
      }
    >
      <b>Price: </b>
      {price?.isClearance ? (
        <ClearancePriceDisplay price={price} canViewPricing={canViewPricing} size={size} />
      ) : (
        <span
          css={css`
            color: ${themeColors.grey};
            font-weight: ${size === "large" ? 200 : "inherit"};
          `}
        >
          {canViewPricing ? price?.formattedPrice ?? "Contact branch" : "Contact branch"}
        </span>
      )}
      {showExclVat && hasPrice && (
        <span
          css={css`
            margin-left: 8px;
          `}
        >
          <ExclVatLabel size={size === "large" ? 13 : 12} />
        </span>
      )}
    </P>
  )
}
